/**
 * Training Consumption Tracking Hook
 *
 * Manages consumption sessions for foundational, browse, and (as of
 * batch 18-E3) ticket-sidebar surfaces. See ALLOWED_CONSUMPTION_SURFACES
 * below — the runtime allow-list mirrors the DB CHECK constraint
 * `tcs_valid_surface` on training_consumption_sessions.
 *
 * =====================================================================
 * CANONICAL EVENT MIGRATION
 * =====================================================================
 * 
 * OLD EVENT TYPES (deprecated):
 * - consumption_started → now use 'opened' via useTrainingEvents
 * - consumption_confirmed → now use 'completed' or 'engaged' via useTrainingEvents
 * 
 * This hook still creates training_consumption_sessions for session tracking,
 * but the event logging now uses the canonical taxonomy from useTrainingEvents.
 * 
 * =====================================================================
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-J: route training-consumption next-reminder query bounds, elapsed
// computations, persisted write timestamps, and postpone-anchor
// derivations through the canonical clock so the consumption-prompt
// flow is deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import type { TrainingAttestation } from '@/lib/training/trainingCompletionOptions';
import {
  type ConfidenceLevel,
  type CompletionStatus,
} from '@/utils/divinationEngine';

// Batch 18-E3: 'ticket_sidebar' is now a first-class consumption surface.
// The ticket-context launch path (TicketTrainingTab → useOpenTrainingWithSession)
// already inserts surface='ticket_sidebar' into training_consumption_sessions;
// the DB CHECK constraint was widened in
// 20260603200000_training_consumption_sessions_allow_ticket_sidebar.sql to
// match. Keeping the union and the DB allow-list in sync here.
export type ConsumptionSurface = 'foundational' | 'browse' | 'ticket_sidebar';

const ALLOWED_CONSUMPTION_SURFACES: readonly ConsumptionSurface[] = [
  'foundational',
  'browse',
  'ticket_sidebar',
] as const;

export interface StartConsumptionOptions {
  technicianProfileId: string;
  artifactId: string;
  artifactType: string;
  expectedDurationMinutes: number | null;
  surface: ConsumptionSurface;
  source?: string;
}

export interface ConsumptionSession {
  id: string;
  technician_profile_id: string;
  artifact_id: string;
  surface: string;
  source: string;
  opened_at: string;
  expected_duration_minutes: number | null;
  confirmed_at: string | null;
  completion_status: CompletionStatus | null;
  completion_confidence: ConfidenceLevel;
  progress_percent: number | null;
  actual_time_spent_minutes: number | null;
  reminder_count: number;
  next_reminder_at: string | null;
  metadata: Record<string, unknown>;
}

export interface ConfirmCompletionData {
  status: 'completed' | 'partial' | 'skipped';
  progressPercent?: number;
  actualTimeSpentMinutes?: number;
  /**
   * K108-R Batch 3: neutral attestation snapshot (userReported vs OptiBay-observed
   * engagement + mismatch flag) merged into the session's `metadata` JSONB. No
   * schema change; completion_status stays within its existing enum. Optional —
   * omitting it leaves session metadata untouched (back-compat).
   */
  attestation?: TrainingAttestation | null;
}

/**
 * Fetch pending consumption sessions for a technician
 */
export function usePendingConsumptionSessions(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['consumption-sessions', 'pending', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .eq('completion_status', 'pending')
        // CORE-AI DoD 11: an expired session is closed for LIFECYCLE purposes —
        // it stops being asked about without any completion judgment.
        .is('expired_at', null)
        .order('opened_at', { ascending: false });
      
      if (error) throw error;
      return data as ConsumptionSession[];
    },
    enabled: !!technicianProfileId,
  });
}

/**
 * Fetch consumption sessions that need confirmation
 * (pending sessions where enough time has passed)
 */
export function useSessionsNeedingConfirmation(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['consumption-sessions', 'needs-confirmation', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(`
          *,
          artifact:training_artifacts(id, title, artifact_type, estimated_time_minutes, provider_name)
        `)
        .eq('technician_profile_id', technicianProfileId)
        .eq('completion_status', 'pending')
        .is('expired_at', null)
        .or(`next_reminder_at.is.null,next_reminder_at.lte.${clock.now().toISOString()}`)
        .order('opened_at', { ascending: false });
      
      if (error) throw error;
      
      // Filter to sessions where expected duration has passed
      return (data || []).filter(session => {
        const openedAt = new Date(session.opened_at);
        const expectedMinutes = session.expected_duration_minutes || 10;
        const elapsedMinutes = (clock.nowMs() - openedAt.getTime()) / (1000 * 60);
        
        // Show confirmation after expected duration + 5 min buffer
        return elapsedMinutes >= expectedMinutes + 5;
      });
    },
    enabled: !!technicianProfileId,
    refetchInterval: 60000, // Check every minute
  });
}

/**
 * Fetch book sessions needing weekly progress update
 */
export function useBookSessionsNeedingProgress(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['consumption-sessions', 'book-progress', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      const oneWeekAgo = clock.now();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(`
          *,
          artifact:training_artifacts(id, title, artifact_type, provider_name)
        `)
        .eq('technician_profile_id', technicianProfileId)
        .in('completion_status', ['pending', 'partial'])
        .or(`next_reminder_at.is.null,next_reminder_at.lte.${clock.now().toISOString()}`)
        .order('opened_at', { ascending: false });
      
      if (error) throw error;
      
      // Filter to book/ebook types
      return (data || []).filter(session => {
        const artifactType = (session.artifact as any)?.artifact_type;
        return ['book', 'ebook'].includes(artifactType);
      });
    },
    enabled: !!technicianProfileId,
  });
}

/**
 * Start a consumption session
 *
 * Batch 18-E3: surface allow-list is now ('foundational', 'browse',
 * 'ticket_sidebar'). The runtime check below is a defensive guard
 * mirroring `ALLOWED_CONSUMPTION_SURFACES` — TypeScript narrows the
 * surface input via `ConsumptionSurface`, but JS callers (and future
 * untyped paths) cannot be trusted, so the check stays.
 */
export function useStartConsumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: StartConsumptionOptions) => {
      if (!ALLOWED_CONSUMPTION_SURFACES.includes(options.surface)) {
        console.error('Consumption tracking blocked for surface:', options.surface);
        return null;
      }
      
      // Create consumption session
      const { data: session, error: sessionError } = await supabase
        .from('training_consumption_sessions')
        .insert({
          technician_profile_id: options.technicianProfileId,
          artifact_id: options.artifactId,
          surface: options.surface,
          source: options.source || 'learning_center',
          expected_duration_minutes: options.expectedDurationMinutes,
          completion_status: 'pending',
          completion_confidence: 'unverified',
          metadata: { artifact_type: options.artifactType },
        })
        .select()
        .single();
      
      if (sessionError) throw sessionError;
      
      // Log 'opened' event using CANONICAL taxonomy
      // NOTE: This replaces the deprecated 'consumption_started' event type
      await supabase
        .from('training_effectiveness_events')
        .insert({
          technician_profile_id: options.technicianProfileId,
          artifact_id: options.artifactId,
          event_type: 'opened', // CANONICAL: was 'consumption_started'
          source: options.source || 'learning_center',
          surface: options.surface,
          metadata: {
            session_id: session.id,
            expected_duration_minutes: options.expectedDurationMinutes,
          },
        });
      
      return session as ConsumptionSession;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['consumption-sessions', 'pending', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Confirm completion of a consumption session
 */
export function useConfirmConsumption() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      sessionId, 
      technicianProfileId,
      artifactId,
      data 
    }: { 
      sessionId: string;
      technicianProfileId: string;
      artifactId: string;
      data: ConfirmCompletionData;
    }) => {
      // Get the session to calculate confidence
      const { data: session, error: fetchError } = await supabase
        .from('training_consumption_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Elapsed time is still measured — but only to RECORD how long the
      // session lasted (actual_time_spent_minutes below), never to conclude
      // anything about the person from it.
      const openedAt = new Date(session.opened_at);
      // CORE-AI DoD 7 — a self-mark is the PERSON'S ASSERTION, not an
      // adjudication. This used to compute confidence in the browser from
      // wall-clock elapsed time and write it: ≥70% of the expected duration
      // minted 'verified', <30% minted 'suspicious'. Neither involved a single
      // observation. The subject of the evidence was adjudicating themselves.
      //
      // What the person did is now recorded where it belongs — completion_status
      // plus manual_marked_complete_at/by — and completion_confidence is left to
      // the governed authorities: promote_extension_confidence() for observed
      // engagement, verify_training_completion() for a manager's attestation.
      // Whatever they have already concluded stands; this write no longer
      // touches it, and the database refuses it if it tries.
      const confidence = (session.completion_confidence ?? 'unverified') as ConfidenceLevel;
      
      // Calculate actual time spent
      const actualTimeSpent = data.actualTimeSpentMinutes ||
        Math.round((clock.nowMs() - openedAt.getTime()) / (1000 * 60));

      // Batch 18-E7: capture WHO took the manual confirmation action
      // for the future manager-verification join (18-E8) and per-shop
      // audit trail. Only populate the new attribution columns when the
      // user explicitly marked the session complete or partial — a
      // 'skipped' click is abandonment, not a manual completion claim.
      // confirmed_at remains the canonical completion timestamp for
      // ALL paths; the new columns are additive attribution.
      const nowIso = clock.now().toISOString();
      const isManualCompletion =
        data.status === 'completed' || data.status === 'partial';
      let manualMarkedCompleteBy: string | null = null;
      if (isManualCompletion) {
        try {
          const { data: authData } = await supabase.auth.getUser();
          manualMarkedCompleteBy = authData?.user?.id ?? null;
        } catch {
          // Auth lookup failure: keep manualMarkedCompleteBy NULL; the
          // UPDATE still records manual_marked_complete_at so the
          // "manual action happened" signal is preserved even when
          // actor identity is unknown.
          manualMarkedCompleteBy = null;
        }
      }

      // K108-R Batch 3: merge the neutral attestation into the session metadata
      // (preserving existing keys) ONLY when provided — completion_status stays
      // within its existing enum; no new status value, no schema change.
      const sessionUpdate: Record<string, unknown> = {
        confirmed_at: nowIso,
        completion_status: data.status === 'skipped' ? 'skipped' : data.status,
        // completion_confidence is deliberately ABSENT — see above.
        progress_percent: data.progressPercent || (data.status === 'completed' ? 100 : null),
        actual_time_spent_minutes: actualTimeSpent,
        updated_at: nowIso,
        manual_marked_complete_at: isManualCompletion ? nowIso : null,
        manual_marked_complete_by: manualMarkedCompleteBy,
      };
      if (data.attestation) {
        const existingMeta = (session.metadata as Record<string, unknown> | null) ?? {};
        // K108-Y: when UPDATING after a resume, never silently drop the prior report
        // — append it to trainingAttestationHistory for auditability. Metadata-only.
        const priorAtt = existingMeta.trainingAttestation;
        const priorHistory = Array.isArray(existingMeta.trainingAttestationHistory)
          ? (existingMeta.trainingAttestationHistory as unknown[])
          : [];
        sessionUpdate.metadata = {
          ...existingMeta,
          trainingAttestation: data.attestation,
          ...(priorAtt ? { trainingAttestationHistory: [...priorHistory, priorAtt] } : {}),
        };
      }

      // Update session
      const { error: updateError } = await supabase
        .from('training_consumption_sessions')
        .update(sessionUpdate)
        .eq('id', sessionId);

      if (updateError) throw updateError;
      
      // Log event using CANONICAL taxonomy
      // NOTE: This replaces the deprecated 'consumption_confirmed' event type
      // Use 'completed' for completed/partial, 'abandoned' for skipped
      const canonicalEventType = data.status === 'skipped' ? 'abandoned' : 'completed';
      
      await supabase
        .from('training_effectiveness_events')
        .insert({
          technician_profile_id: technicianProfileId,
          artifact_id: artifactId,
          event_type: canonicalEventType, // CANONICAL: was 'consumption_confirmed'
          source: session.source,
          surface: session.surface,
          time_spent_seconds: actualTimeSpent * 60,
          metadata: {
            session_id: sessionId,
            completion_status: data.status,
            completion_confidence: confidence,
            progress_percent: data.progressPercent,
            legacy_event: 'consumption_confirmed', // Track migration
          },
        });
      
      return { sessionId, confidence, status: data.status };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['consumption-sessions', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Postpone a consumption session reminder
 */
export function usePostponeReminder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      sessionId, 
      technicianProfileId,
      postponeHours = 24 
    }: { 
      sessionId: string;
      technicianProfileId: string;
      postponeHours?: number;
    }) => {
      const nextReminder = clock.now();
      nextReminder.setHours(nextReminder.getHours() + postponeHours);
      
      // Get current reminder count and increment
      const { data: current } = await supabase
        .from('training_consumption_sessions')
        .select('reminder_count')
        .eq('id', sessionId)
        .single();
      
      const { error } = await supabase
        .from('training_consumption_sessions')
        .update({
          next_reminder_at: nextReminder.toISOString(),
          reminder_count: (current?.reminder_count || 0) + 1,
          updated_at: clock.now().toISOString(),
        })
        .eq('id', sessionId);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['consumption-sessions', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Fetch all consumption sessions for inference calculation
 */
export function useAllConsumptionSessions(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['consumption-sessions', 'all', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(`
          *,
          artifact:training_artifacts(id, artifact_type)
        `)
        .eq('technician_profile_id', technicianProfileId)
        .not('completion_status', 'is', null)
        .not('completion_status', 'eq', 'pending')
        // K108-Z2f: superseded attempts are not real outcomes — exclude them from
        // the divination feed (this is a negative filter, so it would otherwise
        // include 'superseded').
        .not('completion_status', 'eq', 'superseded')
        .order('confirmed_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform for divination engine
      return (data || []).map(session => ({
        id: session.id,
        artifact_id: session.artifact_id,
        artifact_type: (session.artifact as any)?.artifact_type || 'unknown',
        opened_at: session.opened_at,
        confirmed_at: session.confirmed_at,
        completion_status: session.completion_status as CompletionStatus,
        completion_confidence: session.completion_confidence as ConfidenceLevel,
        progress_percent: session.progress_percent,
        expected_duration_minutes: session.expected_duration_minutes,
        actual_time_spent_minutes: session.actual_time_spent_minutes,
      }));
    },
    enabled: !!technicianProfileId,
  });
}
